/**
 * A social post's life on its task: what the server wrote about it on `display`, and the one rule that
 * says where the post is now.
 *
 * Three `display` keys carry a post, all written only by the harness:
 *
 * - `socialPostPreviewCard` — the drafted post (read by `readSocialPostPreviewCard` in entities). It is
 *   written for a post held for the owner's OK AND for one approved ahead (a PENDING task with
 *   `not_before`, `display.origin = "saved-action"`), and it is KEPT after its question is answered: only
 *   a new draft of the same post replaces it.
 * - `postPlan` — the planned post's idea before anything is drafted ({@link readPostPlan}).
 * - `publishedPost` — written once an approved post went out ({@link readPublishedPost}).
 *
 * The post's state is NOT stored anywhere. It is derived from the task alone ({@link postStateOf}), so a
 * client that reads the task can never disagree with the server about it.
 *
 * Import-free on purpose: orion-web's calendar runs this through its shared contract in a bare Node, and
 * the phones mirror it from `native-apps/shared/calendar/contract.json`.
 */

export const POST_STATES = ['idea', 'needsApproval', 'scheduled', 'published', 'dropped', 'failed'];
export const POST_PLAN_FORMATS = ['image', 'carousel', 'video', 'reel', 'text'];
export const POST_PLAN_MEDIA_SOURCES = ['asset', 'ai'];

/** A planned post's idea, before it is drafted. At least one of `contentId` / `network` is present. */

/** What went out: on which network, when, and where it lives. */

export const MAX_POST_PLAN_CONCEPT = 300;
// The server's own caps, counted as it counts them: PostPlan's in code points, PublishedPost's url and postId in UTF-16
// units (Java's String.length, which is JavaScript's .length).
const MAX_CONTENT_ID = 40;
const MAX_POST_ID = 200;
const MAX_URL = 2048;
const NETWORK = /^[a-z0-9][a-z0-9_]{0,39}$/;

/** Length in code points, so an emoji or a non-BMP letter counts once, as the server counts it. */
const codePoints = value => Array.from(value).length;

/** The first `max` code points, never splitting a surrogate pair. */
const cutCodePoints = (value, max) => Array.from(value).slice(0, max).join('');

/** The task's `display` as the server sends it: an untyped Struct. */

const text = value => typeof value === 'string' && value.trim() ? value.trim() : undefined;

/** A network id normalized as the server writes it: lowercase, spaces and `-` as `_` ("google-business" → "google_business"). */
function network(value) {
  var _text;
  const id = (_text = text(value)) == null ? void 0 : _text.toLowerCase().replace(/[\s-]/g, '_');
  return id && NETWORK.test(id) ? id : undefined;
}
function instant(value) {
  const at = text(value);
  return at && Number.isFinite(Date.parse(at)) ? at : undefined;
}
function httpsUrl(value) {
  const raw = text(value);
  if (!raw || raw.length > MAX_URL) {
    return undefined;
  }
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password ? raw : undefined;
  } catch {
    return undefined;
  }
}
function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
}

/**
 * `display.postPlan`, checked: an unknown `format` or `mediaSource` is dropped rather than guessed, the
 * concept is cut to {@link MAX_POST_PLAN_CONCEPT} code points, a content id over 40 is dropped, the network
 * is normalized as the server writes it, and a plan naming neither its content row
 * nor its network is no plan (`null`).
 */
export function readPostPlan(display) {
  const plan = asObject(display == null ? void 0 : display.postPlan);
  if (!plan) {
    return null;
  }
  const contentIdText = text(plan.contentId);
  const contentId = contentIdText && codePoints(contentIdText) <= MAX_CONTENT_ID ? contentIdText : undefined;
  const planNetwork = network(plan.network);
  if (!contentId && !planNetwork) {
    return null;
  }
  const format = POST_PLAN_FORMATS.find(each => each === plan.format);
  const mediaSource = POST_PLAN_MEDIA_SOURCES.find(each => each === plan.mediaSource);
  const conceptText = text(plan.concept);
  const concept = conceptText ? cutCodePoints(conceptText, MAX_POST_PLAN_CONCEPT).trim() : undefined;
  return {
    ...(contentId ? {
      contentId
    } : {}),
    ...(planNetwork ? {
      network: planNetwork
    } : {}),
    ...(format ? {
      format
    } : {}),
    ...(mediaSource ? {
      mediaSource
    } : {}),
    ...(concept ? {
      concept
    } : {})
  };
}

/**
 * `display.publishedPost`, checked: it needs its network and a parseable `publishedAt`, or it is `null`.
 * The URL is kept only when it is https; an unparseable `scheduledAt` and a `postId` over 200 UTF-16 units are dropped.
 */
export function readPublishedPost(display) {
  const post = asObject(display == null ? void 0 : display.publishedPost);
  if (!post) {
    return null;
  }
  const postNetwork = network(post.network);
  const publishedAt = instant(post.publishedAt);
  if (!postNetwork || !publishedAt) {
    return null;
  }
  const url = httpsUrl(post.url);
  const postIdText = text(post.postId);
  const postId = postIdText && postIdText.length <= MAX_POST_ID ? postIdText : undefined;
  const scheduledAt = instant(post.scheduledAt);
  return {
    network: postNetwork,
    publishedAt,
    ...(url ? {
      url
    } : {}),
    ...(postId ? {
      postId
    } : {}),
    ...(scheduledAt ? {
      scheduledAt
    } : {})
  };
}

/** The `display.origin` a task approved ahead through a saved action carries. */
export const SAVED_ACTION_ORIGIN = 'saved-action';

/** The approval answer on a post's decision edge. */
export const APPROVE_CHOICE = 'approve';

/** The fields of a task the rule reads. Satisfied by `AgentTask`. */

/**
 * Where the task's post is, or `undefined` when the task carries no post at all (no `postPlan`, no card,
 * no `publishedPost`) or carries one whose state the task does not tell.
 *
 * In this order, first match wins:
 *
 * 1. `published` — `publishedPost` is present. It is a fact the network gave back, so it outranks the
 *    task's status: a task that failed AFTER the post went out still published it. When the network holds
 *    it for later (`publishedPost.scheduledAt`) it is still `published`: it left Symphony and the network
 *    owns it now, and the reader of `publishedPost` can say "scheduled on the network" from that field.
 * 2. `dropped` — the task is CANCELLED or MOOT.
 * 3. `failed` — the task is FAILED.
 * 4. With a card (one naming its `decisionId`):
 *    - `needsApproval` — that decision is an open blocker on the task (an edge with no `resolvedChoice`);
 *    - `scheduled` — the task is PENDING, no question on it is open, and the decision was answered `approve`
 *      or the task was approved ahead (`display.origin === "saved-action"`);
 *    - otherwise `undefined`: a card the task does not place (a COMPLETED task with no `publishedPost`, an
 *      approved post that is running now) is not given a state it may not be in.
 * 5. `idea` — `postPlan` and no card, on a task that is not COMPLETED (a completed idea with nothing
 *    published says nothing about the post).
 */
export function postStateOf(task) {
  if (!task) {
    return undefined;
  }
  const display = asObject(task.display);
  const card = asObject(display == null ? void 0 : display.socialPostPreviewCard);
  const decisionId = text(card == null ? void 0 : card.decisionId);
  const draftId = decisionId ? undefined : text(card == null ? void 0 : card.draftId);
  const plan = readPostPlan(display);
  if (readPublishedPost(display)) {
    return 'published';
  }
  if (!decisionId && !draftId && !plan) {
    return undefined;
  }
  const status = task.status ?? undefined;
  if (status === 'CANCELLED' || status === 'MOOT') {
    return 'dropped';
  }
  if (status === 'FAILED') {
    return 'failed';
  }
  if (draftId) {
    return 'needsApproval';
  }
  if (decisionId) {
    const edges = task.blockedByDecisions ?? [];
    const edge = edges.find(each => text(each == null ? void 0 : each.decisionId) === decisionId);
    if (edge && !text(edge.resolvedChoice)) {
      return 'needsApproval';
    }
    if (status !== 'PENDING') {
      return undefined;
    }
    // Any other open question still blocks the task, so its post is not scheduled yet.
    const anyOpen = edges.some(each => !!text(each == null ? void 0 : each.decisionId) && !text(each == null ? void 0 : each.resolvedChoice));
    if (anyOpen) {
      return undefined;
    }
    if (edge && text(edge.resolvedChoice) === APPROVE_CHOICE) {
      return 'scheduled';
    }
    if (!edge && text(display == null ? void 0 : display.origin) === SAVED_ACTION_ORIGIN) {
      return 'scheduled';
    }
    return undefined;
  }
  return status === 'COMPLETED' ? undefined : 'idea';
}